import React, { useState, useRef } from "react";
import { FaCamera, FaTimes, FaCheck, FaSpinner } from "react-icons/fa";
import "./ImageScanModal.css";

const VISION_URL = process.env.REACT_APP_VISION_API_URL;

const ImageScanModal = ({ isOpen, onClose, onItemsConfirmed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scannedItems, setScannedItems] = useState(null); // null = not scanned yet
  const fileInputRef = useRef(null);

  const reset = () => {
    setLoading(false);
    setError("");
    setScannedItems(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setLoading(true);
    setError("");
    setScannedItems(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const resp = await fetch(VISION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64: base64, mimeType: file.type }),
      });

      if (!resp.ok) throw new Error(`Worker error ${resp.status}`);
      const data = await resp.json();
      const items = (data.items || []).map((item, i) => ({
        ...item,
        _id: i,
        included: true,
        itemName: item.itemName || "",
        makat: item.makat || "",
        quantity: Number(item.quantity) || 1,
        note: item.note || "",
        isOrganic: null, // null = not set, true = אורגני, false = קווי
      }));

      if (items.length === 0) {
        setError("לא נמצאו פריטים בתמונה. נסה תמונה ברורה יותר.");
        setLoading(false);
        return;
      }

      setScannedItems(items);
    } catch (err) {
      console.error("Scan error:", err);
      setError("שגיאה בסריקה. אנא נסה שוב.");
    }
    setLoading(false);
  };

  const updateItem = (id, field, value) => {
    setScannedItems((prev) =>
      prev.map((item) =>
        item._id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleConfirm = () => {
    const confirmed = scannedItems.filter((i) => i.included);
    if (confirmed.length === 0) {
      setError("לא נבחרו פריטים.");
      return;
    }
    onItemsConfirmed(confirmed);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="image-scan-overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="image-scan-modal">
        <div className="image-scan-header">
          <h3>סרוק תמונה / קבלה</h3>
          <button className="image-scan-close" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <div className="image-scan-body">
          {!scannedItems && !loading && (
            <div className="image-scan-upload-area">
              <p className="image-scan-hint">
                צלם קבלה, תווית או רשימה ידנית — AI יחלץ את הפריטים
              </p>
              {error && <div className="image-scan-error">{error}</div>}
              <button
                className="image-scan-camera-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaCamera /> בחר תמונה / מצלמה
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          )}

          {loading && (
            <div className="image-scan-loading">
              <FaSpinner className="image-scan-spinner" />
              <p>מנתח תמונה...</p>
            </div>
          )}

          {scannedItems && (
            <div className="image-scan-results">
              <div className="image-scan-results-header">
                <p className="image-scan-results-title">
                  נמצאו {scannedItems.length} פריטים — ערוך ואשר:
                </p>
                <div className="image-scan-set-all">
                  <span>הגדר הכל:</span>
                  <button
                    type="button"
                    className="image-scan-organic-btn"
                    onClick={() =>
                      setScannedItems((prev) =>
                        prev.map((i) => ({ ...i, isOrganic: true })),
                      )
                    }
                  >
                    אורגני
                  </button>
                  <button
                    type="button"
                    className="image-scan-kavvi-btn"
                    onClick={() =>
                      setScannedItems((prev) =>
                        prev.map((i) => ({ ...i, isOrganic: false })),
                      )
                    }
                  >
                    קווי
                  </button>
                </div>
              </div>
              {error && <div className="image-scan-error">{error}</div>}
              <div className="image-scan-table-wrap">
                <table className="image-scan-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>שם פריט</th>
                      <th>מק"ט</th>
                      <th>כמות</th>
                      <th>סוג</th>
                      <th>ציין מיקום הפריט או הערה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scannedItems.map((item) => (
                      <tr
                        key={item._id}
                        className={
                          item.included ? "" : "image-scan-row-excluded"
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={item.included}
                            onChange={(e) =>
                              updateItem(item._id, "included", e.target.checked)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="image-scan-field"
                            value={item.itemName}
                            onChange={(e) =>
                              updateItem(item._id, "itemName", e.target.value)
                            }
                            disabled={!item.included}
                          />
                        </td>
                        <td>
                          <input
                            className="image-scan-field image-scan-field-sm"
                            value={item.makat}
                            onChange={(e) =>
                              updateItem(item._id, "makat", e.target.value)
                            }
                            disabled={!item.included}
                          />
                        </td>
                        <td>
                          <input
                            className="image-scan-field image-scan-field-xs"
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateItem(
                                item._id,
                                "quantity",
                                Math.max(1, parseInt(e.target.value) || 1),
                              )
                            }
                            disabled={!item.included}
                          />
                        </td>
                        <td>
                          <div className="image-scan-type-toggle">
                            <button
                              type="button"
                              className={`image-scan-type-btn ${item.isOrganic === true ? "active-organic" : ""}`}
                              onClick={() =>
                                updateItem(item._id, "isOrganic", true)
                              }
                              disabled={!item.included}
                            >
                              או'
                            </button>
                            <button
                              type="button"
                              className={`image-scan-type-btn ${item.isOrganic === false ? "active-kavvi" : ""}`}
                              onClick={() =>
                                updateItem(item._id, "isOrganic", false)
                              }
                              disabled={!item.included}
                            >
                              קו'
                            </button>
                          </div>
                        </td>
                        <td>
                          <input
                            className="image-scan-field"
                            value={item.note}
                            onChange={(e) =>
                              updateItem(item._id, "note", e.target.value)
                            }
                            disabled={!item.included}
                            placeholder="ציין מיקום הפריט או הערה"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="image-scan-actions">
                <button
                  className="image-scan-confirm-btn"
                  onClick={handleConfirm}
                >
                  <FaCheck /> הוסף פריטים (
                  {scannedItems.filter((i) => i.included).length})
                </button>
                <button
                  className="image-scan-rescan-btn"
                  onClick={() => {
                    setScannedItems(null);
                    setError("");
                    fileInputRef.current?.click();
                  }}
                >
                  סרוק שוב
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageScanModal;
